import { styled } from "@mui/material";
import React, { useEffect, useState } from "react";
import { TbChevronRight, TbReportAnalytics } from "react-icons/tb";
import { useLocation } from "react-router-dom";

import { isReportLink, REPORT_GROUPS, reportOf, ReportGroup, ReportMenuItem, searchReportGroups } from "../../pages/reports/reportParams";
import { ActiveSubmenuLink, Branch, SectionToggle, SubmenuLink } from "./sidebar.elements";

const REPORTS_PATH = "/reports";

const Search = styled("input")(() => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  margin: "0.25rem 0",
  padding: "0.5rem 0.75rem",
  border: "1px solid rgba(255, 255, 255, 0.14)",
  borderRadius: "8px",
  outline: "none",
  background: "rgba(255, 255, 255, 0.06)",
  color: "#f9fafb",
  fontFamily: "inherit",
  fontSize: "0.875rem",
  colorScheme: "dark",
  "&::placeholder": { color: "#9ca3af" },
  "&:focus-visible": { borderColor: "#9ca3af" },
}));

const categoryText = { fontSize: "0.875rem", fontWeight: 600, color: "#d1d5db" } as const;

const Category = styled(SectionToggle)(() => ({ ...categoryText, margin: "0.125rem 0 0", padding: "0.45rem 0.75rem" }));

const CategoryLabel = styled("div")(() => ({ ...categoryText, padding: "0.5rem 0.75rem 0.25rem" }));

const Nothing = styled("div")(() => ({ padding: "0.5rem 0.75rem", fontSize: "0.875rem", color: "#9ca3af" }));

const itemPath = (item: ReportMenuItem) => (isReportLink(item) ? item.to : `${REPORTS_PATH}?tab=${item.key}`);

const isCurrent = (item: ReportMenuItem, pathname: string, tab: string | null) =>
  isReportLink(item)
    ? pathname === item.to || pathname.startsWith(`${item.to}/`)
    : pathname === REPORTS_PATH && reportOf(tab).key === item.key;

/** The category holding this page, if any. */
const groupHolding = (pathname: string, tab: string | null): ReportGroup | undefined =>
  REPORT_GROUPS.find((group) => group.items.some((item) => isCurrent(item, pathname, tab)));

/**
 * The Reports group of the dashboard's menu, which opens into the business areas
 * and their reports. One area is open at a time, and arriving at a report opens the
 * way to it. A search lists every match under a plain label, so a closed area cannot
 * hide one. Nothing is remembered between page loads.
 */
export const SidebarReports: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { pathname, search } = useLocation();
  const tab = new URLSearchParams(search).get("tab");
  const holding = groupHolding(pathname, tab)?.title;

  const [open, setOpen] = useState(Boolean(holding));
  const [openGroup, setOpenGroup] = useState<string | null>(holding ?? null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!holding) return;
    setOpen(true);
    setOpenGroup(holding);
  }, [holding]);

  const searching = query.trim() !== "";
  const groups = searchReportGroups(query);

  return (
    <nav aria-label="Reports">
      <SectionToggle
        type="button"
        aria-expanded={open}
        aria-controls="sidebar-section-reports"
        data-current={Boolean(holding)}
        onClick={() => setOpen(!open)}
      >
        <TbReportAnalytics size={18} aria-hidden="true" />
        <span>Reports</span>
        <TbChevronRight size={14} aria-hidden="true" />
      </SectionToggle>
      <div id="sidebar-section-reports">
      {open && (
        <Branch>
          <Search
            type="search"
            aria-label="Find a report"
            placeholder="Find a report"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {groups.map((group) => {
            const listId = `sidebar-reports-${group.title.toLowerCase()}`;
            const expanded = searching || openGroup === group.title;
            return (
              <div key={group.title}>
                {searching ? (
                  <CategoryLabel>{group.title}</CategoryLabel>
                ) : (
                  <Category
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={listId}
                    onClick={() => setOpenGroup(expanded ? null : group.title)}
                  >
                    {group.title}
                    <TbChevronRight size={14} aria-hidden="true" />
                  </Category>
                )}
                <div id={listId} role="list" aria-label={group.title}>
                  {expanded && (
                    <Branch sx={{ marginLeft: "0.75rem" }}>
                      {group.items.map((item) => {
                        const current = isCurrent(item, pathname, tab);
                        const Link = current ? ActiveSubmenuLink : SubmenuLink;
                        return (
                          <div key={item.label} role="listitem">
                            <Link to={itemPath(item)} onClick={onClose} aria-current={current ? "page" : undefined}>
                              {item.label}
                            </Link>
                          </div>
                        );
                      })}
                    </Branch>
                  )}
                </div>
              </div>
            );
          })}
          {groups.length === 0 && <Nothing>No report has that in its name.</Nothing>}
        </Branch>
      )}
      </div>
    </nav>
  );
};
